import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './CommdocConfigPanel.html?raw';

import { TranslationHelpers } from "../../../more/Helpers";
import { SelectEntityDialogTypes } from "../../../moreCore/_loadingHelpers/SelectEntityDialog";
import { VAM2types } from "../../_loadingHelpers/VAMv2Types";
import _uiFrameMixin from '../../../moreCore/UI/Container/uiFrame';
import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import HintBox from '../../../moreCore/UI/HintBox/HintBox';
import _GlobalSelectEntityDialogsMixin from '../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import lang from 'dojo/_base/lang';
import SearchSelect from '../../../moreCore/UI/SearchSelect/SearchSelect';
import RadioGroup from '../../../moreCore/UI/RadioGroup/RadioGroup';
import PrepaymentConfigPanel from './ClassPanels/PrepaymentConfigPanel';

enum LanguageMap {
    _D = "D",
    _E = "E",
    _F = "F",
    _NL = "NL",
}


class CommdocConfigPanel extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private project: VAM2types.EvProjectEntity_Extended;
    private commdocID: string = null;
    private commdocType: VAM2types.CommdocTypes;
    private languageID: string = null;
    private commdocClass: string = null;

    private planningModeSection: HTMLDivElement;
    private isAllPlanningsInput: MdsLax<InstanceType<typeof RadioGroup>>;

    private commdocAddressInput: MdsLax<InstanceType<typeof HintBox>>;
    private commdocLangaugeInput: MdsLax<InstanceType<typeof SearchSelect>>;

    private selectModeSection: HTMLDivElement;
    private selectModeInput: MdsLax<InstanceType<typeof RadioGroup>>;

    private commdocAccountClientInput: MdsLax<InstanceType<typeof HintBox>>;
    private commdocAccountClientSection: HTMLDivElement;

    private classPanelContainer: HTMLDivElement;

    private classConfigPanel: MdsLax<InstanceType<typeof PrepaymentConfigPanel>>;


    setup(project: VAM2types.EvProjectEntity_Extended, commdocType: VAM2types.CommdocTypes) {
        if (!project) {
            console.error("No project provided");
            this.isValid();
            return;
        }
        else if (!commdocType) {
            console.error("No commdoc type provided");
            return;
        }

        let cleanCommdocType = this._getCleanCommdocType(commdocType) as VAM2types.CommdocTypes;
        this._setProject(project);
        this._setCommdocType(cleanCommdocType);

        this.commdocAddressInput.addAdditionalRequestParameter("planningID", this.project.ID);
        this.commdocAddressInput.addAdditionalRequestParameter("commdocType", cleanCommdocType);
        this.setAttributeValue("selectMode", 0);

        this._handleFeatureSections();

        this.setStartupState("ready");

        this._loadDefaultValues(commdocType);

        
        this.show("dlg", `${TranslationHelpers.tryTranslate("CommdocConfigPanel_Title_NewCommdoc")} - ${TranslationHelpers.tryTranslate("CommdocConfigPanel_Title_" + cleanCommdocType)}`);

        this.addButtonEx({
            callback: lang.hitch(this, this._onClickConfirm),
            internalName: "create",
            label: "General_Label_Create",
            styleClass: "primaryBG",
            position: "primary"
        });
    }

    private _getCleanCommdocType(commdocType: VAM2types.CommdocTypes): string {
        let cleanCommdocType = commdocType.replace(/_[A-Z]+$/, "");
        return cleanCommdocType;
    }

    private _handleFeatureSections(): void {
        let canSelectPlanningMode = this.hasFeature("CommdocConfigPanel/SelectPlanningMode");
        this.isAllPlanningsInput.setReadonly(!canSelectPlanningMode);
        this.hideNode(this.planningModeSection, !canSelectPlanningMode);

        let canSelectAccountClient = this.hasFeature("CommdocConfigPanel/SelectAccountClient");
        this.commdocAccountClientInput.setReadonly(!canSelectAccountClient);
        this.hideNode(this.commdocAccountClientSection, !canSelectAccountClient);
        this.commdocAccountClientInput.addAdditionalRequestParameter("planningID", this.project.ID);
        this.commdocAccountClientInput.addAdditionalRequestParameter("commdocType", this.commdocType);
        this.commdocAccountClientInput.addInlineButton(lang.hitch(this, this._onClickSEDButton, "commdocAccountClient"));
        this.commdocAccountClientInput.setMandatory(canSelectAccountClient);
    }

    private _setCanSelectPosEvServ(canSelect: boolean) {
        this.hideNode(this.selectModeSection, !canSelect);
    }

    private _setLanguage(languageID) {
        this.languageID = languageID;
        this.commdocLangaugeInput.setValue(languageID);
    }

    private _setCommdocType(commdocType: VAM2types.CommdocTypes) {
        this.commdocType = commdocType;
        this.setAttributeValue("commdocType", commdocType);
    }

    private _loadDefaultValues(commdocType: VAM2types.CommdocTypes) {
        if (!commdocType) {
            this.showToast({
                title: "CommdocConfigPanel_Error_NoCommdocType",
                iconClass: "fas fa-exclamation-triangle"
            });
        }

        this.StandbyStart();
        let requestData = {
            commdocType: commdocType,
            planningID: this.project.ID
        };
        this.RequestManager.callfunction("Webtool_API_CommdocConfigPanel_GetDefaultValues", requestData, lang.hitch(this, this._onLoadDefaultValuesResponse), lang.hitch(this, this._onLoadDefaultValuesError));
    }

    private _onLoadDefaultValuesResponse(response: { commdocClass: string, commdocLanguageID: string, isAllPlannings: boolean, isOnlyEvBookings: boolean, canSelectPosEvServ: boolean, commdocAccountClient?: any, commdocAddress?: any }) {
        this.StandbyStop();

        this._setCommdocClass(response.commdocClass);
        this._setLanguage(response.commdocLanguageID);
        this.setAttributeValue("isAllPlannings", response.isAllPlannings);
        this.setAttributeValue("isOnlyBookings", response.isOnlyEvBookings);
        this._setCanSelectPosEvServ(response.canSelectPosEvServ);

        if (response.commdocAccountClient) this.setAttributeValue("commdocAccountClient", response.commdocAccountClient);
        if (response.commdocAddress) this.setAttributeValue("commdocAddress", response.commdocAddress);

        this._loadLanguageValues();
    }

    private _onLoadDefaultValuesError(error: more.requestErrorMessage) {
        this.StandbyStop();

        this.showToast({
            title: "General_Error",
            message: error?.Message,
            iconClass: "fas fa-exclamation-triangle"
        });
    }

    private _setProject(project: VAM2types.EvProjectEntity_Extended) {
        this.project = project;
        this.setAttributeValue("planningID", project.ID);
    }

    private _setCommdocClass(commdocClass: string) {
        this.commdocClass = commdocClass;
        this._setupClassPanel();
    }

    private _setupClassPanel() {
        switch (this.commdocClass) {
            default:
                console.warn("No panel defined for this class");
                break;
            case "commdoc_ppmt_from_custom_ev":
                this.classConfigPanel = new PrepaymentConfigPanel();
                this.classConfigPanel.setup(this.project, this.commdocType, this.classPanelContainer);
                this.classConfigPanel.on("changed", lang.hitch(this, this._onClassConfigPanelChange));
                break;
        }
    }

    private _onClassConfigPanelChange(data: Record<string, any>) {
        for (let key of Object.keys(data)) {
            this.setAttributeValue(key, data[key]);
        }

        this._handleConfirmButtonState();
    }

    private _disableDataButtons(disable: boolean = true) {
        this.requestContextButtonDisable("close", disable);
        this.requestContextButtonDisable("create", disable);
    }

    private _onClickConfirm(event: MouseEvent): void {
        this.StandbyStart();
        this._disableDataButtons();
        this.RequestManager.callfunction("Webtool_API_CommdocConfigPanel_CreateCommdoc", this.getCurrentData(), lang.hitch(this, this._onCreateCommdocResponse), lang.hitch(this, this._onCreateCommdocError));
    }

    private _onCreateCommdocResponse(response: { selectionMode?: boolean, commdocID: string, file?: CreatedFileResponse }) {
        this.StandbyStop();
        
        if (!response?.commdocID) {
            this.showToast({
                title: "CommdocConfigPanel_Error_NoCommdoc",
                iconClass: "fas fa-exclamation-triangle",
            });
            return;
        }
        if (response?.file?.error) {
            this.showToast({
                message: response?.file?.error,
                title: "CommdocConfigPanel_Error_CreateFile",
                iconClass: "fas fa-exclamation-triangle",
                timeout: "permanent",
                showClose: true
            });
        }

        this.commdocID = response.commdocID;

        if (!response.selectionMode) {
            this._handleCreatedCommdoc({ commdocID: response.commdocID, file: response.file});
        } else {
            this._onClickSEDButton("selectEvServiceCommdoc", null);
        }
    }

    private _onCreateCommdocError(error: more.requestErrorMessage) {
        this.StandbyStop();
        this._disableDataButtons(false);
        this.showToast({
            title: "General_Error",
            message: error?.Message,
            iconClass: "fas fa-exclamation-triangle"
        });
    }

    private _handleCreatedCommdoc(response: { commdocID: string, file?: FileFinder.FileDescriptor, fileError?: string }) {
        this.emit("commdocCreated", { entityId: response.commdocID, file: response.file });
        this.showToast({
            title: "CommdocConfigPanel_Success_CommdocCreated",
            iconClass: "fas fa-check"
        });
        this.close();
    }

    private _onClickSEDButton(mode: string, event: MouseEvent) {
        let additionalData;

        switch (mode) {
            case "commdocAddress":
            case "commdocAccountClient":
                additionalData = {
                    planningID: this.project.ID,
                    commdocType: this.commdocType,
                    isAllPlannings: this.getAttributeValue("isAllPlannings"),
                    isOnlyBookings: this.getAttributeValue("isOnlyBookings")
                }
                break;
            case "selectEvServiceCommdoc":
                additionalData = {
                    planningID: this.project.ID,
                    commdocID: this.commdocID
                }
                break;
        }

        this.openSelectEntityDialog(event, mode, lang.hitch(this, this._onSEDCallback), null, additionalData);
    }

    private _onSEDCallback(mode: string, data: Array<string>, items: Array<SelectEntityDialogTypes.Item<any>>) {
            switch (mode) {
                case "commdocAddress":
                    if (!items || !items?.length) return;

                    let commdocAddress;
                    commdocAddress = items[0].entry.data;
                    this.commdocAddressInput.setValue(commdocAddress, false);
                    break;
                case "commdocAccountClient":
                    if (!items || !items?.length) return;

                    let commdocAccountClient;
                    commdocAccountClient = items[0].entry.data;
                    this.commdocAccountClientInput.setValue(commdocAccountClient, false);
                    break;
                case "selectEvServiceCommdoc":
                    this._handlePosEvServData(data);
                    break;
            }
    }

    private _handlePosEvServData(posEvServData: Array<string>) {
        if (!posEvServData) {
            this.showToast({
                title: "CommdocConfigPanel_Error_NoPosEvServ",
                iconClass: "fas fa-exclamation-triangle",
            });

            this._handleCreatedCommdoc({ commdocID: this.commdocID });
        }

        let requestData = {
            commdocID: this.commdocID,
            posEvServData: posEvServData
        }

        this.StandbyStart();
        this.RequestManager.callfunction("Webtool_API_CommdocConfigPanel_AddPosEvServData", requestData, lang.hitch(this, this._onCreateCommdocResponse), lang.hitch(this, this._onCreateCommdocError))
    }

    private _handleConfirmButtonState(): void {
        let isValid = this.checkMandatoryInputs() && (!this.classConfigPanel || this.classConfigPanel?.checkMandatoryInputs())
        this.requestContextButtonDisable("create", !isValid);
    }

    protected _onChanged(attribute: string, newValue: any) {
        switch (attribute) {
            case "isAllPlannings":
            case "isOnlyBookings":
                this.commdocAddressInput.addAdditionalRequestParameter(attribute, newValue);
                this.commdocAccountClientInput.addAdditionalRequestParameter(attribute, newValue);
                if (this.hasFeature("CommdocConfigPanel/SelectAccountClient")) this.setAttributeValue("commdocAccountClient", null);
                this.setAttributeValue("commdocAddress", null);
                break;
            case "commdocAddress":
                debugger;
                if (this.commdocClass == "commdoc_ppmt_from_custom_ev") {
                    this.classConfigPanel.setCommdocAddress(newValue?.ref_address);
                }
                break;
        }

        this._handleConfirmButtonState();
    }

    private _loadLanguageValues() {
        this.StandbyStart();

        this.RequestManager.callfunction(
            "Webtool_API_CommdocConfigPanel_GetCommdocLanguages",
            {},
            lang.hitch(this, this._onLoadLanguageValuesResponse),
            lang.hitch(this, this._onLoadLanguageValuesError)
        );
    }

    private _onLoadLanguageValuesResponse(response: { defaultLanguage: Language, data: Array<Language> }) {
        this.StandbyStop();

        this.commdocLangaugeInput.setData(this.languageID, response.data);
    }

    private _onLoadLanguageValuesError(error: more.requestErrorMessage) {
        this.StandbyStop();

        console.error(error?.Message);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.SEDconfigCallfunction = "Webtool_API_CommdocConfigPanel_GetSEDConfig";
        this.commdocAddressInput.addInlineButton(lang.hitch(this, this._onClickSEDButton, "commdocAddress"));
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(CommdocConfigPanel);